import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterEvent } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { setUser } from '@sentry/angular';
import { SikoSapOrderActions } from '@siko/features/my-account/sap-order-detail/store/actions';
import { StateWithSikoSapOrder } from '@siko/features/my-account/sap-order-detail/store/siko-sap-order-state';
import { SikoUser } from '@siko/models/user.model';
import { SikoUserActivityService } from '@siko/shared/services/user-activity.service';
import { SikoDialogService } from '@siko/shared/utils/siko-dialog.service';
import { SikoExponeaService } from '@siko/shared/utils/siko-exponea.service';
import { SikoTrackingUtils } from '@siko/shared/utils/siko-tracking-utils';
import {
    AuthService,
    EventService,
    CurrencyService,
    isNotUndefined,
    LoginEvent,
    LogoutEvent,
    RoutingService,
    SiteContextActions,
    User,
    WindowRef,
} from '@spartacus/core';
import { StorefrontComponentModule } from '@spartacus/storefront';
import { UserProfileFacade } from '@spartacus/user/profile/root';
import dayjs from 'dayjs';
import { distinctUntilChanged, filter, interval, map, Observable, Subscription, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { POST_REGISTRATION_LOGIN_KEY } from '@siko/constants';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-root',
    templateUrl: './app.component.html',
    imports: [StorefrontComponentModule],
})
export class AppComponent implements OnInit, OnDestroy {
    title = 'b2b';

    currentUrl = '';

    isUserLogged = false;

    sapCustomerGroup = 'none';

    bonusPointsToExpire = 0;

    bonusProgramCurrentPoints = 0;

    logoutEventListener$ = this.eventService.get(LogoutEvent).subscribe(() => {
        setUser(null);

        this.stateWithSapOrderStore.dispatch(new SikoSapOrderActions.ResetSapOrderState());

        void this.routingService.go('/login');
    });

    intervalSubscription?: Subscription;

    routeIsChanged = false;

    loggedIn$: Observable<boolean> = this.authService.isUserLoggedIn();

    private readonly inactivityLogoutTimeout = 3600; // 60 minutes in seconds

    private readonly timeoutRepeat = 60 * 1000; // 1 minute in milliseconds

    constructor(
        readonly winRef: WindowRef,
        readonly dialogService: SikoDialogService,
        readonly actions: Actions,
        readonly userProfileFacade: UserProfileFacade,
        readonly authService: AuthService,
        readonly trackingUtils: SikoTrackingUtils,
        readonly router: Router,
        readonly eventService: EventService,
        readonly exponeaService: SikoExponeaService,
        private readonly userActivityService: SikoUserActivityService,
        readonly routingService: RoutingService,
        readonly stateWithSapOrderStore: Store<StateWithSikoSapOrder>,
        readonly currencyService: CurrencyService
    ) {
        // Spartacus doesn't update the currency after login – see B2XT-668 for more details.
        this.eventService
            .get(LoginEvent)
            .pipe(
                switchMap(() =>
                    this.userProfileFacade.get().pipe(
                        filter(isNotUndefined),
                        map((user: User | undefined) => ({
                            currencyIsocode: user?.currency?.isocode,
                            userId: user?.uid,
                        })),
                        filter(({ currencyIsocode, userId }) =>
                            isNotUndefined(currencyIsocode) && isNotUndefined(userId)
                        ),
                        distinctUntilChanged((prev, curr) => prev.currencyIsocode === curr.currencyIsocode)
                    )
                ),
                takeUntilDestroyed()
            )
            .subscribe(({ currencyIsocode, userId }) => {
                this.actionsAfterLogin(userId!);
                this.currencyService.setActive(currencyIsocode!);
            });

        this.handlePostLoginFromStorage()
    }

    changeLanguageSubscription: Subscription = this.actions
        .pipe(ofType(SiteContextActions.LANGUAGE_CHANGE))
        .subscribe(() => {
            this.trackingUtils.pushSikoPageEvent(
                this.isUserLogged,
                this.currentUrl,
                this.sapCustomerGroup,
                this.bonusPointsToExpire,
                this.bonusProgramCurrentPoints
            );
        });

    ngOnInit(): void {
        this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(event => {
            this.routeIsChanged = true;
            this.authService
                .isUserLoggedIn()
                .subscribe(data => {
                    this.isUserLogged = data;
                })
                .unsubscribe();

            if (event instanceof RouterEvent) {
                this.currentUrl = event.url;
                this.trackingUtils.pushResetEvent();

                this.userProfileFacade
                    .get()
                    .pipe(filter(isNotUndefined))
                    .subscribe(data => {
                        if (event.url.includes('login')) {
                            return;
                        }

                        const sikoUser: SikoUser | undefined = data;

                        if (sikoUser.orgUnit?.sikoSapCustomerGroup) {
                            this.sapCustomerGroup = sikoUser.orgUnit.sikoSapCustomerGroup;
                        }

                        this.bonusPointsToExpire = sikoUser?.bonusPointsToExpire ?? 0;
                        this.bonusProgramCurrentPoints = sikoUser?.sikoBonusPoints ?? 0;

                        if (this.routeIsChanged) {
                            this.trackingUtils.pushSikoPageEvent(
                                this.isUserLogged,
                                event.url,
                                this.sapCustomerGroup,
                                this.bonusPointsToExpire,
                                this.bonusProgramCurrentPoints
                            );
                            this.routeIsChanged = false;
                        }
                    });

                if (event.url.includes('login')) {
                    this.trackingUtils.pushSikoPageEvent(this.isUserLogged, event.url, 'none', 0, 0);
                    this.routeIsChanged = false;
                }

                this.dialogService.dismissAll('Change of route');
            }
        });
    }

    checkUsersInActivity(): void {
        this.intervalSubscription?.unsubscribe();
        this.intervalSubscription = interval(this.timeoutRepeat)
            .pipe(
                switchMap(() => this.loggedIn$),
                filter((isLoggedIn: boolean) => isLoggedIn)
            )
            .subscribe(() => {
                const currentDate = dayjs();
                const lastActiveDate = this.userActivityService.getLastActiveDate();

                if (currentDate.diff(lastActiveDate, 'second') > this.inactivityLogoutTimeout) {
                    this.intervalSubscription?.unsubscribe();
                    void this.authService.coreLogout();
                }
            });
    }

    handlePostLoginFromStorage(): void {
        const raw = this.winRef.sessionStorage?.getItem(POST_REGISTRATION_LOGIN_KEY);

        if (!raw) {
            return;
        }

        const { userId } = JSON.parse(raw) as {
            userId: string;
        };

        this.winRef.sessionStorage?.removeItem(POST_REGISTRATION_LOGIN_KEY);

        this.authService.isUserLoggedIn()
            .pipe(
                filter(Boolean),
                take(1)
            )
            .subscribe(() => {
                this.actionsAfterLogin(userId);
            });
    }

    ngOnDestroy(): void {
        this.logoutEventListener$.unsubscribe();
        this.changeLanguageSubscription.unsubscribe();
    }

    actionsAfterLogin(userId: string): void {
        setUser({ email: userId });
        this.exponeaService.identify(userId)
        this.checkUsersInActivity();
    }

}
